/**
 * Lead-form public API key: masked by default, reveal, copy, regenerate (with confirm).
 */
import React, { useState, useCallback } from 'react';
import Check from 'lucide-react/dist/esm/icons/check';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import Key from 'lucide-react/dist/esm/icons/key';
import { Button, ConfirmationDialog, Spinner } from '../../../components/ui';

type Tr = (key: string) => string;

export interface SalesPublicApiKeyBlockProps {
  tr: Tr;
  apiKey: string | null;
  loading?: boolean;
  error?: boolean;
  compact?: boolean;
  onRegenerate: () => void | Promise<void>;
  regenerating?: boolean;
}

const maskKey = (key: string) => '•'.repeat(key.length);

const iconBtnClass =
  'shrink-0 p-1.5 rounded-lg hover:bg-surface-elevated text-content-muted border-none outline-none cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

const SalesPublicApiKeyBlock: React.FC<SalesPublicApiKeyBlockProps> = ({
  tr,
  apiKey,
  loading = false,
  error = false,
  compact = false,
  onRegenerate,
  regenerating = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const handleCopy = useCallback(() => {
    if (!apiKey) return;
    void navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [apiKey]);

  const handleConfirmRegen = useCallback(async () => {
    setConfirmRegen(false);
    try {
      await onRegenerate();
      setVisible(true);
    } catch {
      /* parent may toast; keep visibility unchanged */
    }
  }, [onRegenerate]);

  const titleClass = compact ? 'text-xs font-medium text-content' : 'text-sm font-semibold text-content';
  const helpClass = compact ? 'text-[11px] text-content-muted mb-2' : 'text-[11px] text-content-muted mb-4';
  const codeClass = compact ? 'text-[11px] font-mono' : 'text-xs font-mono';

  return (
    <div className={compact ? '' : 'p-5 rounded-2xl bg-surface-primary border border-subtle'}>
      <h3 className={`${titleClass} mb-1 flex items-center gap-2`}>
        <Key size={compact ? 12 : 16} className="text-amber-400" />
        {tr('settingsOrgsLeadApiKeyTitle')}
      </h3>
      <p className={helpClass}>{tr('settingsOrgsLeadApiKeyHelp')}</p>

      {loading ? (
        <Spinner size="sm" />
      ) : (
        <div className="space-y-2">
          {error && <p className="text-xs text-status-danger">{tr('settingsOrgsLeadApiKeyError')}</p>}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <div className="flex flex-1 min-w-0 items-center gap-1 p-3 rounded-xl bg-surface-page border border-subtle">
              <code
                className={`flex-1 min-w-0 ${codeClass} text-content break-all ${visible && apiKey ? 'select-all' : 'select-none tracking-widest'}`}
              >
                {apiKey ? (visible ? apiKey : maskKey(apiKey)) : '—'}
              </code>
              <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                className={iconBtnClass}
                disabled={!apiKey}
                title={visible ? tr('settingsOrgsLeadApiKeyHide') : tr('settingsOrgsLeadApiKeyShow')}
                aria-label={visible ? tr('settingsOrgsLeadApiKeyHide') : tr('settingsOrgsLeadApiKeyShow')}
              >
                {visible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className={iconBtnClass}
                disabled={!apiKey}
                title={tr('settingsOrgsLeadApiKeyCopy')}
                aria-label={tr('settingsOrgsLeadApiKeyCopy')}
              >
                {copied ? <Check size={14} className="text-status-success" /> : <Copy size={14} />}
              </button>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0 w-full sm:w-auto"
              loading={regenerating}
              disabled={regenerating}
              onClick={() => setConfirmRegen(true)}
            >
              {tr('settingsOrgsLeadApiKeyRegenerate')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmRegen}
        onClose={() => setConfirmRegen(false)}
        onConfirm={handleConfirmRegen}
        title={tr('settingsOrgsLeadApiKeyRegenerateConfirmTitle')}
        message={tr('settingsOrgsLeadApiKeyRegenerateConfirmMessage')}
        confirmText={tr('settingsOrgsLeadApiKeyRegenerate')}
        cancelText={tr('settingsTenantsBtnCancel')}
        variant="warning"
      />
    </div>
  );
};

export default React.memo(SalesPublicApiKeyBlock);
